/** The itinerary record's one-step actions (plan O1, D6, §9.8): view mode only — in edit mode
 *  `published` is a draft field — each audited, refused for the operator's placeholder and
 *  for an itinerary without an operator, and all behind the catalog edit kill switch
 *  (`ConsoleCatalogEditEnabled`: off answers the changeset's own 400 `disabled`).
 *
 *  - **Publish now / Unpublish now** — `POST /itineraries/{pk}/publish/` / `unpublish/`: sets
 *    `published` on the itinerary and its catalog itinerary in one transaction — never through
 *    the funnel, so a missing catalog itinerary is NOT created (the app then shows "Coming
 *    soon"). 200 (`ItineraryPublishResult`): the audit row, the consequences in words, the
 *    follow-ups and the re-read detail. 409 `drift` when it already is what was asked (someone
 *    did it first); 409 `record_locked` when a concurrent save held its rows (retryable as is).
 *  - **Regenerate deep link** — `POST /itineraries/{pk}/deep_link/regenerate/`: 202
 *    (`ItineraryDeepLinkRegenerateAccepted`, with the link and the stored QR before it runs) —
 *    the Branch.io task is queued once the request commits; it updates the link (same address,
 *    the current name and cover) or makes one, stores its QR code and then rebuilds the app
 *    cache. No task id: the record shows the outcome once it is reloaded (a few seconds) —
 *    `useDeepLinkWatch` reloads it. 400 `deep_link_not_configured` when the operator has no
 *    Branch.io key and secret; 409 `record_locked` as above. It has no "already so": it can
 *    always be asked again, so any other refusal is the server's own, in its words
 *    (`itineraryRegenerateRefusal`) — never Publish's same-state refresh.
 *
 *  `useSetItineraryPublished` keeps the record's reads in step: a publish re-seeds the detail
 *  from its answer (refetched when the server could not re-read it) and refetches
 *  edit_context; a same-state refusal refetches both (the page shows what the server holds);
 *  the kill switch's refusal (either action's) refetches the edit catalog and edit_context, so
 *  Edit says it is switched off. */
import { useMutation, useQueryClient, type QueryClient, type UseMutationResult } from '@tanstack/react-query';

import { ConsoleApiError, apiPost } from './client';
import type { ItineraryDetail } from './itineraries';
import { itineraryDetailKey, itineraryRecordKeys } from './itineraryRecord';
import type { components } from './types';

type Schemas = components['schemas'];

/** The 200 of Publish now / Unpublish now, its `detail` in the shape the record reads. */
export type ItineraryPublishResult = Omit<Schemas['ItineraryPublishResult'], 'detail'> & {
  detail: ItineraryDetail | null;
};
export type ItineraryDeepLinkRegenerateAccepted = Schemas['ItineraryDeepLinkRegenerateAccepted'];
export type ItineraryRecordActionError = Schemas['ItineraryRecordActionError'];
export type ItineraryRecordActionBody = Schemas['ItineraryRecordActionBody'];

export function setItineraryPublished(
  id: number,
  published: boolean,
  body: ItineraryRecordActionBody = {},
): Promise<ItineraryPublishResult> {
  return apiPost<ItineraryPublishResult>(`/itineraries/${id}/${published ? 'publish' : 'unpublish'}/`, body);
}

export function regenerateItineraryDeepLink(
  id: number,
  body: ItineraryRecordActionBody = {},
): Promise<ItineraryDeepLinkRegenerateAccepted> {
  return apiPost<ItineraryDeepLinkRegenerateAccepted>(`/itineraries/${id}/deep_link/regenerate/`, body);
}

/** Why an action did nothing, by what the operator can do about it:
 *  - `same_state` — Publish now / Unpublish now only — 409 `drift`: it already is what was
 *    asked (changed on the server meanwhile);
 *  - `locked` — 409 `record_locked`: someone else is saving the itinerary; try again;
 *  - `disabled` — 400 `disabled`: the kill switch is off;
 *  - `not_configured` — 400 `deep_link_not_configured`: no Branch.io key and secret;
 *  - `refused` — any other refusal with the server's words (the placeholder, no operator, a
 *    bad body);
 *  - `failed` — no answer the console can read (a network error, a 500). */
export type ItineraryActionRefusalKind = 'same_state' | 'locked' | 'disabled' | 'not_configured' | 'refused' | 'failed';

export interface ItineraryActionRefusal<K extends ItineraryActionRefusalKind = ItineraryActionRefusalKind> {
  kind: K;
  /** The server's sentence, or the error's message. */
  detail: string;
}

/** Regenerate deep link's refusals: no "already so" — it can always be asked again. */
export type ItineraryRegenerateRefusal = ItineraryActionRefusal<Exclude<ItineraryActionRefusalKind, 'same_state'>>;

function actionRefusal(e: unknown, { sameState }: { sameState: boolean }): ItineraryActionRefusal {
  if (!(e instanceof ConsoleApiError)) {
    return { kind: 'failed', detail: e instanceof Error && e.message ? e.message : 'The request failed.' };
  }
  const body = e.body as { code?: unknown; detail?: unknown } | undefined;
  const code = typeof body?.code === 'string' ? body.code : null;
  const detail = typeof body?.detail === 'string' && body.detail ? body.detail : e.message;
  if (e.status === 409 && code === 'record_locked') return { kind: 'locked', detail };
  if (e.status === 409 && sameState) return { kind: 'same_state', detail };
  if (e.status === 400 && code === 'disabled') return { kind: 'disabled', detail };
  if (e.status === 400 && code === 'deep_link_not_configured') return { kind: 'not_configured', detail };
  if (e.status >= 400 && e.status < 500 && code) return { kind: 'refused', detail };
  return { kind: 'failed', detail };
}

/** Why Publish now / Unpublish now did nothing: any 409 but `record_locked` is "already so". */
export function itineraryActionRefusal(e: unknown): ItineraryActionRefusal {
  return actionRefusal(e, { sameState: true });
}

/** Why Regenerate deep link did nothing — its own mapping: a 409 other than `record_locked`
 *  is the server's refusal in its words (`refused`), never Publish's `same_state` (which
 *  refetches the record, edit_context included, mid-edit). */
export function itineraryRegenerateRefusal(e: unknown): ItineraryRegenerateRefusal {
  return actionRefusal(e, { sameState: false }) as ItineraryRegenerateRefusal;
}

/** The record's reads after an action changed nothing because the server holds something
 *  else: the detail and edit_context (same state); the edit catalog too (the kill switch). */
function refreshAfterRefusal(qc: QueryClient, id: number, refusal: ItineraryActionRefusal): void {
  if (refusal.kind === 'same_state') {
    void qc.invalidateQueries({ queryKey: itineraryDetailKey(id) });
    void qc.invalidateQueries({ queryKey: itineraryRecordKeys.editContext(id) });
  } else if (refusal.kind === 'disabled') {
    void qc.invalidateQueries({ queryKey: itineraryRecordKeys.catalog() });
    void qc.invalidateQueries({ queryKey: itineraryRecordKeys.editContext(id) });
  }
}

/** Publish now (`true`) / Unpublish now (`false`). Success re-seeds the detail from the answer
 *  (refetched when it carries none), refetches edit_context (the `published` baseline) and the
 *  itineraries' other reads — never the used-by summary, which publishing cannot change. */
export function useSetItineraryPublished(
  id: number,
): UseMutationResult<ItineraryPublishResult, unknown, { published: boolean }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ published }: { published: boolean }) => setItineraryPublished(id, published),
    onSuccess: (result) => {
      if (result.detail) qc.setQueryData(itineraryDetailKey(id), result.detail);
      void qc.invalidateQueries({ queryKey: itineraryRecordKeys.editContext(id) });
      void qc.invalidateQueries({
        queryKey: ['itineraries'],
        predicate: ({ queryKey: [, kind, key] }) =>
          kind !== 'usedBySummary' && !(result.detail && kind === 'detail' && key === id),
      });
    },
    onError: (e) => refreshAfterRefusal(qc, id, itineraryActionRefusal(e)),
  });
}

/** Regenerate deep link. 202: the task is queued — the page reloads the detail to show its
 *  outcome (`useDeepLinkWatch`). */
export function useRegenerateItineraryDeepLink(
  id: number,
): UseMutationResult<ItineraryDeepLinkRegenerateAccepted, unknown, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => regenerateItineraryDeepLink(id),
    onError: (e) => refreshAfterRefusal(qc, id, itineraryRegenerateRefusal(e)),
  });
}
